import { useEffect, useState } from 'react';
import { ColumnSelectOption } from 'lib/components/ColumnSelect';
import { TextFieldProps } from 'lib/components/TextField';
import { Popover } from 'lib/components/Popover';
import { AutocompleteInput } from './AutocompleteInput';
import { AutocompletePanel } from './AutocompletePanel';

// This component implements an accessible "combobox" pattern described here: https://www.w3.org/WAI/ARIA/apg/patterns/combobox/

type BaseAutocompleteProps<T extends string> = {
  id: string;
  /** A list of options to show in the autocomplete dropdown */
  options: ColumnSelectOption<T>[];
  /** Show a loading state */
  loading?: boolean;
  noOptionsMessage?: string;
} & Omit<TextFieldProps, 'value' | 'onChange'>;

type SingleSelectAutocompleteProps = {
  /** The selected option or list of options */
  value?: string;
  /** Set the current value of the input */
  onChange: (value: string) => void;
  /** Allow multiple selections */
  multiselect?: false;
} & BaseAutocompleteProps<string>;

type MultiSelectAutocompleteProps = {
  /** The selected option or list of options */
  value?: string[];
  /** Set the current value of the input */
  onChange: (value: string[]) => void;
  /** Allow multiple selections */
  multiselect: true;
} & BaseAutocompleteProps<string>;

export type AutocompleteProps =
  | SingleSelectAutocompleteProps
  | MultiSelectAutocompleteProps;

export function Autocomplete(props: AutocompleteProps) {
  const { id, options, loading, ...textFieldProps } = props;

  const [query, setQuery] = useState<string | null>(null);

  const selectedOptions = options.filter(option =>
    props.multiselect
      ? props.value?.includes(option.value)
      : option.value === props.value
  );

  const valueLabel = selectedOptions.map(option => option.label).join(', ');

  const filteredOptions = query
    ? options.filter(option =>
        option.label.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const highlightedOption = filteredOptions[highlightedIndex];

  useEffect(() => {
    !props.multiselect && resetQuery();
  }, [props.multiselect, valueLabel]);

  function onQueryChange(query: string) {
    setQuery(query);
    setHighlightedIndex(0);
  }

  function selectOption(newSelection: ColumnSelectOption<string>) {
    const selectedOptionIndex = options.indexOf(newSelection);
    setHighlightedIndex(selectedOptionIndex);
    if (props.multiselect) {
      if (props.value?.includes(newSelection.value)) {
        props.onChange(
          props.value.filter(option => option !== newSelection.value)
        );
      } else {
        props.onChange([...(props.value || []), newSelection.value]);
      }
    } else {
      props.onChange(newSelection.value);
    }
    !props.multiselect && resetQuery();
  }

  function selectHighlightedOption() {
    if (highlightedOption) {
      selectOption(highlightedOption);
    }
  }

  function onInputFocus() {
    if (!props.multiselect) {
      const selectedOptionIndex = options.indexOf(selectedOptions[0]);
      setHighlightedIndex(selectedOptionIndex);
    } else {
      setHighlightedIndex(0);
    }
  }

  function onInputBlur() {
    if (highlightedOption && query) {
      selectHighlightedOption();
    } else {
      const fieldIsClear = query === '';
      fieldIsClear && !props.multiselect ? props.onChange('') : resetQuery();
    }
  }

  function hightlightNextOption() {
    setHighlightedIndex(prevIndex =>
      Math.min(prevIndex + 1, filteredOptions.length - 1)
    );
  }

  function hightlightPreviousOption() {
    setHighlightedIndex(prevIndex => Math.max(prevIndex - 1, 0));
  }

  function resetQuery() {
    setQuery(null);
  }

  const inputValue = props.multiselect ? query : query ?? valueLabel ?? '';

  return (
    <Popover
      id={id}
      popoverType="listbox"
      fullWidth
      activator={
        <AutocompleteInput
          id={id}
          selectedOptions={selectedOptions}
          selectOption={selectOption}
          multiselect={props.multiselect || false}
          onArrowDown={hightlightNextOption}
          onArrowUp={hightlightPreviousOption}
          onEnter={selectHighlightedOption}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          {...textFieldProps}
          value={inputValue || ''}
          onChange={onQueryChange}
          placeholder={loading ? 'Loading...' : textFieldProps.placeholder}
        />
      }
    >
      <AutocompletePanel
        loading={loading}
        filteredOptions={filteredOptions}
        selectedOptions={selectedOptions}
        highlightedIndex={highlightedIndex}
        selectOption={selectOption}
        multiselect={props.multiselect || false}
        noOptionsMessage={textFieldProps.noOptionsMessage}
      />
    </Popover>
  );
}
